import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, Star } from 'lucide-react';
import { useAuth } from '../auth/AuthContext';
import { getEnrollment } from '../../lib/lms';
import { lmsHref } from '../../lib/surface';
import { listCourseReviews, ratingSummary, getMyReview, saveReview, type Review } from '../../lib/reviews';
import { Panel, PanelHead, Stamp } from '../lms/ui';
import { Skeleton } from '../lms/Skeletons';

interface ReviewsSectionProps {
  courseId: string;
}

const Stars: React.FC<{ value: number; size?: string }> = ({ value, size = 'h-3.5 w-3.5' }) => (
  <span className="inline-flex items-center gap-0.5" aria-label={`${value.toFixed(1)} out of 5`}>
    {[1, 2, 3, 4, 5].map((n) => (
      <Star
        key={n}
        className={`${size} ${n <= Math.round(value) ? 'text-manila' : 'text-rule'}`}
        fill={n <= Math.round(value) ? 'currentColor' : 'none'}
        strokeWidth={1}
        aria-hidden="true"
      />
    ))}
  </span>
);

const ReviewsSection: React.FC<ReviewsSectionProps> = ({ courseId }) => {
  const { user } = useAuth();
  const [reviews, setReviews] = useState<Review[]>([]);
  const [loading, setLoading] = useState(true);
  const [canReview, setCanReview] = useState(false);
  const [mine, setMine] = useState<Review | null>(null);
  const [form, setForm] = useState({ rating: 5, title: '', body: '' });
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const rows = await listCourseReviews(courseId);
      setReviews(rows);
      if (user) {
        const [enr, own] = await Promise.all([
          getEnrollment(user.id, courseId).catch(() => null),
          getMyReview(user.id, courseId).catch(() => null),
        ]);
        setCanReview(!!enr && (enr as { status?: string }).status !== 'revoked');
        setMine(own);
        if (own) setForm({ rating: own.rating, title: own.title, body: own.body });
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseId, user]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;
    setBusy(true);
    setError('');
    setDone('');
    try {
      await saveReview({ userId: user.id, courseId, rating: form.rating, title: form.title, body: form.body });
      setDone(mine ? 'Your review was updated.' : 'Thank you — your review is live.');
      setOpen(false);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save your review.');
    } finally {
      setBusy(false);
    }
  };

  const summary = ratingSummary(reviews);

  return (
    <Panel>
      <PanelHead
        title="Student reviews"
        action={
          <span className="font-mono text-[9px] uppercase tracking-[0.1em] text-ink-2">
            {summary.count === 0 ? 'No reviews yet' : 'From enrolled students'}
          </span>
        }
      />

      {loading ? (
        <div className="space-y-3 px-5 py-8" role="status" aria-busy="true" aria-label="Loading student reviews">
          <span className="sr-only">Loading student reviews…</span>
          <Skeleton className="h-4 w-48 rounded-md" />
          <Skeleton className="h-3 w-full rounded-md" />
          <Skeleton className="h-3 w-4/5 rounded-md" />
        </div>
      ) : (
        <>
          {summary.count > 0 && (
            <div className="grid gap-5 px-5 py-5 sm:grid-cols-[160px_minmax(0,1fr)] sm:items-center">
              <div>
                <div className="flex items-end gap-2">
                  <p className="tnum text-[32px] font-semibold leading-none tracking-[-0.035em] text-ink">
                    {summary.average.toFixed(1)}
                  </p>
                  <span className="mb-0.5 font-mono text-[9.5px] uppercase tracking-[0.1em] text-ink-2">/ 5</span>
                </div>
                <div className="mt-2">
                  <Stars value={summary.average} size="h-4 w-4" />
                </div>
                <p className="mt-1.5 font-mono text-[9.5px] uppercase tracking-[0.1em] text-ink-2">
                  {summary.count} {summary.count === 1 ? 'review' : 'reviews'}
                </p>
              </div>

              <div className="space-y-1.5">
                {[5, 4, 3, 2, 1].map((n) => {
                  const count = summary.distribution[n - 1];
                  const pct = Math.round((count / summary.count) * 100);
                  return (
                    <div key={n} className="grid grid-cols-[12px_minmax(0,1fr)_30px] items-center gap-2">
                      <span className="text-right font-mono text-[9.5px] text-ink-2">{n}</span>
                      <div className="flex h-[7px] gap-px" role="img" aria-label={`${n} star: ${count} reviews`}>
                        {Array.from({ length: 20 }).map((_, i) => (
                          <span key={i} className={`h-full flex-1 ${i < Math.round(pct / 5) ? 'bg-manila' : 'bg-rule'}`} />
                        ))}
                      </div>
                      <span className="tnum font-mono text-[9.5px] text-ink-2">{count}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <div className="border-t border-rule px-5 py-4">
            {!user ? (
              <p className="text-[12.5px] leading-5 text-ink-2">
                <a href={lmsHref('/login')} className="lms-link">Sign in or open a student account</a> to leave a review.
              </p>
            ) : canReview ? (
              <>
                {!open && (
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <p className="text-[12.5px] leading-5 text-ink-2">
                      {mine ? 'You have a review on this programme.' : 'Completed this programme? Add an honest review.'}
                    </p>
                    <button onClick={() => setOpen(true)} className="lms-btn-secondary !py-2 !text-[12px]">
                      {mine ? 'Edit my review' : 'Write a review'}
                    </button>
                  </div>
                )}

                {open && (
                  <form onSubmit={submit}>
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <h3 className="text-[13px] font-semibold text-ink">{mine ? 'Edit your review' : 'Write a review'}</h3>
                      <button type="button" onClick={() => setOpen(false)} className="font-mono text-[9px] uppercase tracking-[0.1em] text-ink-2 hover:text-ink">
                        Close
                      </button>
                    </div>

                    <div className="mt-3">
                      <span className="lms-label">Your rating</span>
                      <div className="flex items-center gap-1" role="radiogroup" aria-label="Rating">
                        {[1, 2, 3, 4, 5].map((n) => (
                          <button
                            key={n}
                            type="button"
                            role="radio"
                            aria-checked={form.rating === n}
                            onClick={() => setForm({ ...form, rating: n })}
                            aria-label={`${n} star${n === 1 ? '' : 's'}`}
                            className={`flex h-8 w-8 items-center justify-center border ${
                              n <= form.rating ? 'border-manila/40 bg-manila-soft text-manila' : 'border-rule bg-white text-rule'
                            }`}
                          >
                            <Star className="h-4 w-4" fill="currentColor" strokeWidth={1} />
                          </button>
                        ))}
                      </div>
                    </div>

                    <div className="mt-3 grid gap-3">
                      <div>
                        <label htmlFor="review-headline" className="lms-label">Headline (optional)</label>
                        <input
                          id="review-headline"
                          value={form.title}
                          onChange={(e) => setForm({ ...form, title: e.target.value })}
                          placeholder="A short headline"
                          className="lms-field"
                        />
                      </div>
                      <div>
                        <label htmlFor="review-body" className="lms-label">Review</label>
                        <textarea
                          id="review-body"
                          value={form.body}
                          onChange={(e) => setForm({ ...form, body: e.target.value })}
                          rows={3}
                          placeholder="What did you learn? Who would you recommend it to?"
                          className="lms-field resize-y leading-5"
                        />
                      </div>
                    </div>

                    {error && <p className="mt-3 border border-danger/25 bg-danger-soft px-3 py-2 text-[12px] text-danger" role="alert">{error}</p>}

                    <div className="mt-3 flex flex-wrap items-center gap-2">
                      <button disabled={busy} className="lms-btn-primary !py-2 !text-[12px]">
                        {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" strokeWidth={1.5} />}
                        {busy ? 'Saving…' : mine ? 'Update review' : 'Publish review'}
                      </button>
                      <button type="button" onClick={() => setOpen(false)} className="lms-btn-quiet !py-2 !text-[12px]">
                        Cancel
                      </button>
                    </div>
                  </form>
                )}
              </>
            ) : (
              <p className="text-[12.5px] leading-5 text-ink-2">
                Reviews come from enrolled students.{' '}
                <Link to="/courses" className="lms-link">Explore the programme register</Link> to join them.
              </p>
            )}

            {done && (
              <p className="mt-3 border border-success/25 bg-success-soft px-3 py-2 text-[12px] text-success" role="status">{done}</p>
            )}
            {error && !open && (
              <p className="mt-3 border border-danger/25 bg-danger-soft px-3 py-2 text-[12px] text-danger" role="alert">{error}</p>
            )}
          </div>

          {reviews.length > 0 && (
            <div className="divide-y divide-rule border-t border-rule">
              {reviews.map((r) => (
                <article key={r.id} className="px-5 py-4">
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
                    <span className="text-[12.5px] font-semibold text-ink">{r.author_name || 'Verified learner'}</span>
                    <Stamp tone="cleared">Verified learner</Stamp>
                    <Stars value={r.rating} />
                    <span className="font-mono text-[9px] uppercase tracking-[0.1em] text-ink-2">
                      {new Date(r.created_at).toLocaleDateString('en-NG', { day: 'numeric', month: 'short', year: 'numeric' })}
                    </span>
                  </div>
                  {r.title && <p className="mt-2 text-[13px] font-semibold leading-5 text-ink">{r.title}</p>}
                  {r.body && <p className="mt-1 whitespace-pre-wrap text-[12.5px] leading-5 text-ink-2">{r.body}</p>}
                </article>
              ))}
            </div>
          )}
        </>
      )}
    </Panel>
  );
};

export default ReviewsSection;
